import React, { useState, useMemo, useCallback, useEffect } from "react";
import styles from "../../LoanRequestPage.module.scss";
import TabSelector from "../../../../../common/components/tab-selector/TabSelector";
import { BackIcon, ChervonLeft } from "../../../../../assets/icons";
import IdentityTab from "./DetailsTabs/IdentityTab";
import AddressTab from "./DetailsTabs/AddressTab";
import EmploymentDetailsTab from "./DetailsTabs/EmploymentDetailsTab";
import FinancialInformationTab from "./DetailsTabs/FinancialInformationTab";
import UIModal from "../../../../../common/components/UI/Modal/UIModal";
import { OrderType } from "../../../../../model/orders/orders";
import moment from "moment";
import { toast } from "react-toastify";
import ordersService from "../../../../../httpService/OrdersService";
import LoanProductOtpForm from "../../../loan-products/otp-form/LoanProductOtpForm";
import CommonDialog from "../../../../../common/components/dialog/CommonDialog";
import { showLoader, hideLoader } from "../../../../../stores/slices/loader-slice";
import { useDispatch } from "react-redux";
import { CreditEngineScore } from "../../../../../model/customer/customer";
import customerService from "../../../../../httpService/CustomerService";

type Props = {
	setOrderDetails: (value: null) => void;
	orderDetails: OrderType;
	refetchData: () => void;
};

const LoanRequestDetails = (props: Props) => {
	const { orderDetails, setOrderDetails, refetchData } = props;
	const [selectedTab, setSelectedTab] = useState(1);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [rejectReason, setRejectReason] = useState("");
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
	const [creditEngineScore, setCreditEngineScore] = useState<CreditEngineScore>();
	const dispatch = useDispatch();

	const tabs = [
		{ id: 1, title: "Identity", onClick: () => setSelectedTab(1) },
		{ id: 2, title: "Address", onClick: () => setSelectedTab(2) },
		{ id: 3, title: "Employment Details", onClick: () => setSelectedTab(3) },
		{ id: 4, title: "Financial Information", onClick: () => setSelectedTab(4) },
	];

	const renderTabContent = () => {
		switch (selectedTab) {
			case 1:
				return <IdentityTab orderDetails={orderDetails} />;
			case 2:
				return <AddressTab orderDetails={orderDetails} />;
			case 3:
				return <EmploymentDetailsTab orderDetails={orderDetails} />;
			default:
				return <FinancialInformationTab orderDetails={orderDetails} creditEngineScore={creditEngineScore} />;
		}
	};

	const formatNumber = (num?: number) => {
		if (num === undefined || num === null) return "N/A";

		const value = num.toFixed(2);

		return value.replace(/\d(?=(\d{3})+\.)/g, "$&,");
	};

	const fetchCreditEngineScore = useCallback(async () => {
		try {
			const result = await customerService.getCreditEngineScore({
				customerId: orderDetails.customer.id,
				from: new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000).toISOString(),
			});
			setCreditEngineScore(result.data);
		} catch (error) {
			console.error("Failed to fetch credit engine score", error);
		}
	}, [orderDetails.customer.id]);

	useEffect(() => {
		fetchCreditEngineScore();
	}, [fetchCreditEngineScore]);

	const handleApiCall = async (actionCode: string | null, stepName = "", isAccept = false) => {
		dispatch(showLoader());
		setShowRejectModal(false);

		const data: any = {
			orderId: orderDetails.id,
			actionCode: actionCode,
		};

		if (!isAccept) {
			data.description = rejectReason;
		}

		ordersService
			.post(isAccept ? "accept" : "decline", data)
			.then(result => {
				if (result.data.actionCodeSent) {
					toast.success("OTP sent successfully");
					isAccept ? setAcceptDialogOpen(true) : setRejectDialogOpen(true);
				}
				if (stepName === "verified") {
					const successMessage = isAccept ? "Loan request accepted successfully" : "Loan request rejected successfully";
					toast.success(successMessage);
					setOrderDetails(null);
					setRejectDialogOpen(false);
					refetchData();
				}
			})
			.finally(() => {
				dispatch(hideLoader());
			})
			.catch(error => {
				toast.error(error.response.data.title || "Something went wrong. Please try again.");
			});
	};

	const handleSubmit = async () => {
		if (!rejectReason) {
			toast.error("Please enter a reason for rejecting the loan request");
			return;
		}
		handleApiCall(null);
	};

	const resendOtp = async () => {
		handleApiCall(null);
	};

	const verifyOtp = async (otp: string, isAccept?: boolean) => {
		handleApiCall(otp, "verified", isAccept);
	};

	return (
		<section className={styles.loanRequestPage}>
			<div className={styles.pageTitle}>
				<div className={styles.icon} onClick={() => setOrderDetails(null)}>
					<BackIcon />
				</div>
				{orderDetails.customer.firstName} {orderDetails.customer.lastName}
			</div>
			<div className={styles.contentWrapper}>
				<div className={styles.cardsContainer}>
					<div className={`${styles.card} ${styles.totalRequestCard}`}>
						<div className={styles.cardHeader}>Total Loan Amount</div>
						<div className={styles.cardBody}>
							<span>{formatNumber(orderDetails.costOverview.totalAmount)}</span>
							<span className={styles.currency}>NGN</span>
						</div>
					</div>
					<div className={`${styles.card} ${styles.pendingRequestCard}`}>
						<div className={styles.cardHeader}>Capital</div>
						<div className={styles.cardBody}>
							<span>{formatNumber(orderDetails.monthlyPaymentDetails?.deposit)}</span>
							<span className={styles.currency}>NGN</span>
						</div>
					</div>
					<div className={`${styles.card} ${styles.approvedRequestCard}`}>
						<div className={styles.cardHeader}>Interest</div>
						<div className={styles.cardBody}>
							<span>{formatNumber(orderDetails.monthlyPaymentDetails?.monthlyPayment)}</span>
							<span className={styles.currency}>NGN</span>
						</div>
					</div>
					<div className={`${styles.card} ${styles.declinedRequestCard}`}>
						<div className={styles.cardHeader}>Application Date</div>
						<div className={`${styles.cardBody} ${styles.date}`}>
							<span>{moment(orderDetails.createdAt).format("h:mm A")}</span>
							<span>{moment(orderDetails.createdAt).format("MMM DD, YYYY")}</span>
						</div>
					</div>
				</div>

				<div className={styles.actionButtons}>
					{selectedTab === 4 ? (
						<>
							<button
								className={`standardButton ${styles.declineButton}`}
								onClick={() => setShowRejectModal(true)}
								disabled={orderDetails.status === "Declined"}
								title={orderDetails.status === "Declined" ? "This loan request has already been rejected" : ""}
							>
								<ChervonLeft /> Reject Offer
							</button>
							<button
								className={`standardButton ${styles.approveButton}`}
								onClick={() => handleApiCall(null, "", true)}
								disabled={orderDetails.status === "Approved"}
								title={orderDetails.status === "Approved" ? "This loan request has already been accepted" : ""}
							>
								Accept Offer <ChervonLeft />
							</button>
						</>
					) : null}
				</div>

				<div className={styles.tabSelectorWrapper}>
					<div className={styles.tabContainer}>
						<TabSelector tabs={tabs} selectedTab={selectedTab} theme="yellow" />
					</div>
					{renderTabContent()}

					<div className={styles.buttonsWrapper}>
						{selectedTab !== 1 ? (
							<button className={`standardButton ${styles.previousButton}`} onClick={() => setSelectedTab(selectedTab - 1)}>
								<ChervonLeft /> <span>Previous</span>
							</button>
						) : (
							<div></div>
						)}
						{selectedTab !== 4 ? (
							<button className={`standardButton ${styles.nextButton}`} onClick={() => setSelectedTab(selectedTab + 1)}>
								Next
							</button>
						) : (
							<div></div>
						)}
					</div>
				</div>
			</div>

			{showRejectModal && (
				<UIModal title="Reject Loan Request" onClose={() => setShowRejectModal(false)} onSubmit={handleSubmit}>
					<div className={styles.rejectLoanRequest}>
						<p>Please enter the reason for rejecting the customer request</p>
						<textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}></textarea>
					</div>
				</UIModal>
			)}

			{rejectDialogOpen && (
				<CommonDialog>
					<LoanProductOtpForm onResendOtp={resendOtp} onVerifyOtp={verifyOtp} onCloseDialog={() => setRejectDialogOpen(!rejectDialogOpen)} />
				</CommonDialog>
			)}

			{acceptDialogOpen && (
				<CommonDialog>
					<LoanProductOtpForm onResendOtp={resendOtp} onVerifyOtp={(otp: string) => verifyOtp(otp, true)} onCloseDialog={() => setAcceptDialogOpen(!acceptDialogOpen)} />
				</CommonDialog>
			)}
		</section>
	);
};

export default LoanRequestDetails;
